import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography, Grid, Tooltip } from "@mui/material";
import { DataGrid, GridAlignment } from "@mui/x-data-grid";
import EmptyResultDataGrid from "../../../utils/ui-components/EmptyResultDataGrid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinearProgress from "@mui/material/LinearProgress";
import moment from "moment";
import { useTheme } from "@mui/material/styles";
import { currencyFormat } from "../../../utils/utils";
import { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { getState } from "../../../redux/actions/actions";
import { ReportService } from "../../../assets/_services/report-service";
import ViewEditDialog from "../../../utils/ui-components/ViewEditDialog";
import ApproveSmsBulkCampaign from "./ReviewAndConfirm";

export interface SearchInterface {
  fromDate: Dayjs | string;
  toDate: Dayjs | string;
  campaignStatus: number[];
  messageType: number;
  user: { value: number | string; label: string };
  maskName: string;
}

function ApprovePendingBulkCampaings({ state, value, optOutMessage }: { state: any; value: number; optOutMessage: string }) {
  const authState = useSelector((state: any) => state.auth.authData);
  const user = getState(authState);
  const theme: any = useTheme();
  const [campaingData, setCampaignData]: any = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [usesData, setUsesData]: any[] = useState([]);
  const [open, setOpen] = useState(false);

  const [paginationModel, setPaginationModel] = useState(
    state?.paginationModel || {
      page: 0,
      pageSize: 10,
    }
  );

  useEffect(() => {
    window.history.replaceState({}, "");
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [paginationModel]);

  const fetchData = () => {
    setIsLoading(true);
    if (Number(user.role) === 3) {
      ReportService.getAllFirstApproverCampaings(paginationModel.page, paginationModel.pageSize).then((response) => {
        if (response.isSuccess) {
          setUsesData(response.data.data.rows);
          setCount(response?.data?.data?.count || 0);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      });
    } else {
      ReportService.getAllSecondApproverCampaings(paginationModel.page, paginationModel.pageSize).then((response) => {
        if (response.isSuccess) {
          setUsesData(response.data.data.rows);
          setCount(response?.data?.data?.count || 0);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      });
    }
  };

  const dialog = useMemo(
    () =>
      ViewEditDialog(ApproveSmsBulkCampaign)({
        open: open,
        setOpen: setOpen,
        dialogTitle: "Campaign Details",
        initialItem: campaingData,
        fetchData: fetchData,
        theme: theme,
        maxWidth: "md",
        initialData: { role: user.role },
      }),
    [open]
  );

  const columns = [
    {
      field: "action",
      filterable: false,
      headerName: "",
      sortable: false,
      disableExport: false,
      width: 50,
      headerClassName: "stickyActionHeader",
      cellClassName: "stickyColumn",
      renderCell: (params: any) => {
        return (
          <>
            <Tooltip title="View Delivery Report" placement="left">
              <IconButton
                onClick={() => {
                  setCampaignData({
                    ...params.row,
                    channel_name: Number(params.row.is_personalized_campaign) === 1 ? "Multi Channels" : params.row.channel_name,
                    message:
                      params.row.campaign_type === 0
                        ? params.row.message
                        : `${params.row.message}\n${optOutMessage.replace("#_MASK", params.row?.mask ?? "")}`,
                  });
                  setOpen(true);
                }}
              >
                <VisibilityIcon fontSize="inherit" color="primary" />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      field: "id",
      filterable: false,
      headerName: "ID",
      sortable: false,
      disableExport: false,
      minWidth: 80,
      flex: 1,
    },
    {
      field: "campaign_ref_id",
      filterable: false,
      headerName: "Referece ID",
      sortable: false,
      disableExport: false,
      minWidth: 250,
      flex: 3,
    },
    {
      field: "campaign_name",
      filterable: false,
      headerName: "Campaign Name",
      sortable: false,
      disableExport: false,
      minWidth: 200,
      flex: 2,
    },
    {
      field: "userName",
      filterable: false,
      headerName: "User Name",
      sortable: false,
      disableExport: false,
      minWidth: 180,
      flex: 2,
    },
    {
      field: "campaignType",
      filterable: false,
      headerName: "Campaign Type",
      sortable: false,
      disableExport: false,
      align: "center" as GridAlignment,
      minWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return params.row.campaign_type === 0 ? (
          <Typography variant="overline" fontWeight={"bold"} color={"primary"}>
            Transactional
          </Typography>
        ) : (
          <Typography variant="overline" fontWeight={"bold"} color={"orange.main"}>
            Promotional
          </Typography>
        );
      },
    },
    {
      field: "mask",
      filterable: false,
      headerName: "Mask",
      sortable: false,
      disableExport: false,
      minWidth: 130,
      flex: 1,
    },
    // {
    //   field: "campaignStatus",
    //   filterable: false,
    //   headerName: "Status",
    //   sortable: false,
    //   disableExport: false,
    //   minWidth: 180,
    //   flex: 1,
    //   align: "center" as GridAlignment,
    //   renderCell: (params: any) => {
    //     return (
    //       <Typography variant="overline" fontWeight={"bold"} color={findCampaignStatus(params.row.campaign_status).color}>
    //         {findCampaignStatus(params.row.campaign_status).status}
    //       </Typography>
    //     );
    //   },
    // },
    {
      field: "channel_name",
      filterable: false,
      headerName: "Channel",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 1,
      renderCell: (params: any) => {
        return Number(params.row.is_personalized_campaign) === 1 ? "Multi Channels" : params.row.channel_name;
      },
    },
    {
      field: "createdTimeStamp",
      filterable: false,
      headerName: "Created AT",
      sortable: false,
      disableExport: false,
      minWidth: 130,
      flex: 2,
      renderCell: (params: any) => {
        return params.row.created_timestamp ? (
          <div style={{ textAlign: "center", width: "100%", paddingRight: 10 }}>
            <Typography variant="caption" fontWeight={"bold"}>
              {moment.utc(params.row.created_timestamp).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
          </div>
        ) : (
          <div style={{ textAlign: "center", width: "100%", paddingRight: 10 }}>-</div>
        );
      },
    },
    {
      field: "scheduledTimeStamp",
      filterable: false,
      headerName: "Schedule AT",
      sortable: false,
      disableExport: false,
      minWidth: 130,
      flex: 2,
      renderCell: (params: any) => {
        return params.row.scheduled_timestamp ? (
          <div style={{ textAlign: "center", width: "100%", paddingRight: 10 }}>
            <Typography variant="caption" fontWeight={"bold"}>
              {moment.utc(params.row.scheduled_timestamp).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
          </div>
        ) : (
          <div style={{ textAlign: "center", width: "100%", paddingRight: 10 }}>-</div>
        );
      },
    },
    {
      field: "dialog_count",
      filterable: false,
      headerName: "Dialog Base",
      sortable: false,
      disableExport: false,
      minWidth: 120,
      flex: 2,
      align: "right" as GridAlignment,
    },
    {
      field: "mobitel_count",
      filterable: false,
      headerName: "Mobitel Base",
      sortable: false,
      disableExport: false,
      minWidth: 140,
      flex: 2,
      align: "right" as GridAlignment,
    },
    {
      field: "airtel_count",
      filterable: false,
      headerName: "Airtel Base",
      sortable: false,
      disableExport: false,
      minWidth: 120,
      flex: 2,
      align: "right" as GridAlignment,
    },
    {
      field: "hutch_count",
      filterable: false,
      headerName: "Hutch Base",
      sortable: false,
      disableExport: false,
      minWidth: 120,
      flex: 2,
      align: "right" as GridAlignment,
    },
    {
      field: "total_number_count",
      filterable: false,
      headerName: "Total Target Base",
      sortable: false,
      disableExport: false,
      minWidth: 140,
      flex: 2,
      renderCell: (params: any) => {
        return <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>{params.row.total_number_count}</div>;
      },
    },
    {
      field: "initial_campaign_cost",
      filterable: false,
      headerName: "Campaign Cost",
      sortable: false,
      disableExport: false,
      minWidth: 150,
      flex: 2,
      renderCell: (params: any) => {
        return (
          <div style={{ textAlign: "right", width: "100%", paddingRight: 10 }}>{currencyFormat(Number(params.row.initial_campaign_cost), "", 2)}</div>
        );
      },
    },
  ];

  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xs={11.9}>
        <Box sx={{ minHeight: 400, width: "100%", mt: 1 }}>
          <DataGrid
            autoHeight
            columns={columns}
            loading={isLoading}
            rows={usesData}
            slots={{
              noRowsOverlay: EmptyResultDataGrid,
              loadingOverlay: () => <LinearProgress />,
            }}
            style={{ overflow: "auto" }}
            sx={{
              "& .MuiDataGrid-cell.stickyColumn": {
                position: "sticky", // Sticky cells
                left: 0, // Pin to the left
                backgroundColor: `${theme.palette.mode === "light" ? theme.palette.primary.light : theme.palette.background.paper}`,
                zIndex: 1, // Ensure the sticky column is on top of others
              },
              cursor: "pointer",
            }}
            pagination
            getRowId={(row) => row.id}
            paginationMode="server"
            rowCount={count}
            onCellClick={(params) => {
              setCampaignData({
                ...params.row,
                channel_name: Number(params.row.is_personalized_campaign) === 1 ? "Multi Channels" : params.row.channel_name,
                message:
                  params.row.campaign_type === 0
                    ? params.row.message
                    : `${params.row.message}\n${optOutMessage.replace("#_MASK", params.row?.mask ?? "")}`,
              });
              setOpen(true);
            }}
            disableRowSelectionOnClick
            pageSizeOptions={[5, 10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </Box>
      </Grid>
      {dialog}
    </Grid>
  );
}

export default ApprovePendingBulkCampaings;
